import Sampler from './Sampler.js';
import Tone from 'tone';
import SamplesStream from 'track/SamplesStream.js';
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import {loadParameterFromJson, saveParameterToJson} from 'parameters/parameterJsonFunctions.js';
import {AudioParamValueParameter} from 'parameters/AbstractAudioParamParameter.js';

class SimpleSampler extends Sampler {
    constructor(track, sample, onNewSampler, slices, samplerData) {
        super(track, sample, onNewSampler, slices, samplerData, true);
        this.parameters = [];

        this.onParameter = new AbstractParameter(
            'on', this,
            (o, x) => [],
            {
                min: false,
                max: true,
                unit: '',
                defaultValue: false,
            });

        this.registerParameter(this.onParameter);

        this.loopParameter = new AbstractParameter(
            'loop', this,
            (o, x) => [],
            {
                min: false,
                max: true,
                unit: '',
                defaultValue: false,
            });

        this.registerParameter(this.loopParameter);

        this.loadFromJson(samplerData);

        this.startTime = undefined;
        this.endTime  = undefined;

        this.samplesStream = new SamplesStream(
            sample, track.sequencerEngine);

        this.isSimple = true;
    }

    get gainNode() {
        return this.sampler.output;
    }

    auditionSound(sample) {
        this.sample = sample;
        this.audioBuffer = sample.audioBuffer;
        this.baseBuffer = new Tone.Buffer(sample.audioBuffer);
        this.sampler._buffers._buffers[48] = this.baseBuffer;
        this.slice(0, 0, this.getDuration());
        this.registerParameter(this.volumeParameter);
    }

    init(samplerData=this.samplerData, slices=this.sliceData, skipAnalyze=false) {
        super.init(samplerData, slices, skipAnalyze);
        this.sampler = new Tone.Sampler({"C3": this.baseBuffer},{curve:'linear'});
        this.reversedBuffer = new Tone.Buffer();
        if (this.baseBuffer.numberOfChannels === 1) {
            this.reversedBuffer.fromArray(
                this.baseBuffer.getChannelData(0));
        } else {
            this.reversedBuffer.fromArray([
                this.baseBuffer.getChannelData(0), 
                this.baseBuffer.getChannelData(1)]);
        }
        this.reversedBuffer.reverse = true;
        this.reversedSampler = new Tone.Sampler({"C3": this.reversedBuffer},{curve:'linear'});

        // is volume even used for anything anymore?
        // We pass the output from the SamplerVoices manager (connected to the amp env.)
        // directly to the triggerAttack/AttackRelease calls so that they
        // dont use an extra volume node
        this.volumeParameter = new AudioParamValueParameter(
                'volume', this.sampler.output.gain, {
                    min: 0,
                    max: 1,
                    defaultValue: 1
                });
        this.registerParameter(this.volumeParameter);

        return this;
    }

    dispose() {
        this.sample.dispose();
        this.sampler.dispose();
        this.baseBuffer.dispose();
        this.reversedBuffer.dispose();
        this.reversedSampler.dispose();
        this.slices = [];
        this.detuneSignal.dispose();
        this.pitchEnvelopes.forEach(
            p => p.dispose());

        this.sample = undefined;
        this.sampler = undefined;
        this.baseBuffer = undefined;
        this.pitchEnvelopes = undefined;
        this.detuneSignal = undefined;
    }

    isDisposed() {
        return this.sampler === undefined;
    }

    getSlice() {
        return {
            buffer: this.baseBuffer,
            startTime: this.startTime,
            endTime: this.endTime,
            getDuration: () => this.endTime - this.startTime
        };
    }

    get() {
        return this.sampler;
    }

    newSlice(startTime, endTime) {
        this.startTime = startTime;
        this.endTime = endTime;

        this.slices.push(new Slice(this.baseBuffer, startTime, endTime));
        this.sliceData.push({startTime: startTime, endTime: endTime});
        this.slice(this.slices.length - 1, startTime, endTime, true);
        return this.slices[this.slices.length - 1];
    }

    slice(sliceNumber, startTime, endTime, skipCheck=false) {
        var sampleSlice = this.slices[sliceNumber];
        endTime = endTime == undefined ? this.getDuration()  : endTime; 
        sampleSlice.startTime = startTime;
        sampleSlice.endTime = endTime;
        var duration = (endTime - startTime) * this.alpha;
        this.sliceData[sliceNumber] = {
            startTime: startTime,
            endTime: endTime
        };

        this.startTime = startTime;
        this.endTime = endTime;
        this.updateSlicesState();
    }

    loadFromJson(samplerData) {
        this.onParameter.set(samplerData.on === undefined ? true : samplerData.on);

        loadParameterFromJson(samplerData, 'onParameter', this.onParameter);

        this.loop = samplerData.loop || false;

        loadParameterFromJson(samplerData, 'loopParameter', this.loopParameter);

        this.detune = samplerData.detune || 0;
        loadParameterFromJson(samplerData, 'detuneParameter', this.detuneParameter);
    }

    set detune(o) {
        this.detuneParameter.set(o);
    }

    get detune() {
        return this.detuneParameter.get();
    }

    set on(o) {
        this.onParameter.set(o);
    }

    get on() {
        return this.onParameter.get();
    }

    set loop(o) {
        this.loopParameter.set(o);
    }

    get loop() {
        return this.loopParameter.get();
    }

    getJson() {
        let json = {
            ... super.getJson(),
            on: this.onParameter.get(),
            loop: this.loop,
            detune: this.detune,
        };

        json = saveParameterToJson(json, 'onParameter', this.onParameter);
        json = saveParameterToJson(json, 'loopParameter', this.loopParameter);
        json = saveParameterToJson(json, 'detuneParameter', this.detuneParameter);
        return json;
    }

    async triggerAttack(note, time, velocity, loopPoints, pitchEnvelope, output, isReversed=false) {
        note = this.getNoteFromTranspose(note);
        let sampler = isReversed ? this.reversedSampler : this.get(0);
        let {start}  = loopPoints;
        start = this.convertToStartTime(0, start);
        start += this.startTime;
        sampler.loop = this.loop;
        let {loopStart, loopEnd} = this.getLoopPoints(loopPoints);
        start = loopStart;
        sampler.loopStart = loopStart;
        sampler.loopEnd = loopEnd;
        let pitchEnvelopes = [
            // this.getPitchEnvelope(sampler, note, time, velocity, this.track.trackData)
        ];
        if (pitchEnvelope) {
            pitchEnvelopes.push(pitchEnvelope);
        }

        this.triggerWaveformAnimation(
            time,
            undefined,
            0,
            0,
            start);
 
        await super.triggerAttack(sampler, note, time, velocity, pitchEnvelopes, start, output, isReversed);
    }

    async triggerAttackRelease(
        note,
        duration,
        time,
        velocity,
        loopPoints,
        pitchEnvelope,
        output,
        warpStart=0,
        isReversed=false
    ) {
        note = this.getNoteFromTranspose(note);

        let {start} = loopPoints;
        start = this.convertToStartTime(0, start);
        start += this.startTime;
        let sampler = isReversed ? this.reversedSampler : this.get(0);
        sampler.loop = this.loop;
        let {loopStart, loopEnd} = this.getLoopPoints(loopPoints);
        start = loopStart;
        start += this.convertToStartTime(0, warpStart);
        sampler.loopStart = loopStart;
        sampler.loopEnd = loopEnd;

        this.triggerWaveformAnimation(
            time,
            duration,
            0,
            0,
            start);

        let pitchEnvelopes = [
            // this.getPitchEnvelope(sampler, note, time, velocity, this.track.trackData)
        ];
        if (pitchEnvelope) {
            pitchEnvelopes.push(pitchEnvelope);
        }
        await super.triggerAttackRelease(sampler, note, duration, time, velocity, pitchEnvelopes, start, output, isReversed);
    }

    triggerRelease(notes, isReversed=false) {
        this.triggerReleaseWaveformAnimation();
        notes = notes.map(note => this.getNoteFromTranspose(note));
        var sampler = isReversed ? this.reversedSampler : this.get(0);
        sampler.triggerRelease(notes, Tone.Master.context.currentTime);
    }

    getLoopPoints({loopStart, loopLength}) {
        loopStart = this.startTime + this.getDuration()  * (loopStart / 100.0);
        let loopEnd = loopStart + this.getDuration()  * (loopLength / 100.0);
        return {
            loopStart,
            loopEnd: Math.min(loopEnd, this.endTime),
        };
    }

    // loopstart/looplength given as percentages of duration
    setLoopPoints(points) {
        let {loopStart, loopEnd} = this.getLoopPoints(points);
        for (let midi in this.sampler._activeSources) {
            let sources = this.sampler._activeSources[midi];
            sources.forEach(s => {
                s.loopStart = loopStart;
                s.loopEnd = loopEnd;
            });
        }
    }
}

class Slice {
    constructor(buffer, startTime, endTime) {
        this.buffer = buffer;
        this.originalBuffer = buffer;
        this.startTime = startTime;
        this.endTime = endTime;
    }

    getCopy() {
        return new Slice(this.buffer, this.startTime, this.endTime);
    }

    getJson() {
        return {
            buffer: this.buffer,
            startTime: this.startTime,
            endTime: this.endTime,
        };
    }

    dispose() {
    }
}



export default SimpleSampler;
